/**
 * LiveMomentoSwitcher — Botón flotante grande para cambiar de momento en modo
 * Live sin salir del pad. Al pulsarlo, sheet inferior con las 4 subpestañas
 * y contadores. Diseñado fat-finger para iPad en modo Live.
 */
import React, { useState } from 'react';
import { Music2, Wine, UtensilsCrossed, PartyPopper, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from './ui/sheet';
import { haptic } from '../utils/haptic';
import { BUCKET_LABELS, countByBucket, getVisibleBuckets } from '../utils/momentoBuckets';

const BUCKET_ICONS = {
  ceremonia: Music2,
  coctel: Wine,
  banquete: UtensilsCrossed,
  baile: PartyPopper,
};

const BUCKET_COLORS = {
  ceremonia: 'from-purple-600 to-purple-800 text-white',
  coctel:    'from-amber-600 to-amber-800 text-white',
  banquete:  'from-emerald-600 to-emerald-800 text-white',
  baile:     'from-pink-600 to-pink-800 text-white',
};

export const LiveMomentoSwitcher = ({
  peticiones = [],
  activeBucket = 'baile',
  onChange,
  overrides = {},
}) => {
  const [open, setOpen] = useState(false);
  const counts = countByBucket(peticiones);
  const visible = getVisibleBuckets(counts, overrides);
  const ActiveIcon = BUCKET_ICONS[activeBucket] || PartyPopper;

  // Si sólo hay 1 bucket visible, no tiene sentido el switcher
  if (visible.length <= 1) return null;

  const handleSelect = (bucket) => {
    haptic(15);
    if (typeof onChange === 'function') onChange(bucket);
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          onClick={() => haptic(10)}
          className={`
            fixed z-40 bottom-4 right-4 flex items-center gap-2
            px-5 py-3 rounded-full shadow-2xl
            bg-gradient-to-br ${BUCKET_COLORS[activeBucket] || BUCKET_COLORS.baile}
            font-bold text-base
            active:scale-95 transition-transform
            border-2 border-white/20
          `}
          data-testid="live-momento-switcher-btn"
          aria-label="Cambiar momento activo"
        >
          <ActiveIcon className="w-5 h-5" />
          <span>{BUCKET_LABELS[activeBucket]}</span>
          <ChevronUp className="w-4 h-4 opacity-80" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="bg-slate-950 border-slate-800 text-white pb-safe"
        data-testid="live-momento-switcher-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-white text-center text-lg">
            Cambiar momento
          </SheetTitle>
        </SheetHeader>
        <div className="grid grid-cols-2 gap-3 pt-4 pb-6">
          {visible.map((bucket) => {
            const Icon = BUCKET_ICONS[bucket];
            const isActive = bucket === activeBucket;
            return (
              <button
                key={bucket}
                onClick={() => handleSelect(bucket)}
                className={`
                  min-h-[100px] p-4 rounded-xl border-2
                  flex flex-col items-center justify-center gap-2
                  bg-gradient-to-br ${BUCKET_COLORS[bucket]}
                  ${isActive ? 'ring-4 ring-white/40 scale-105' : 'opacity-80'}
                  active:scale-95 transition-transform font-bold
                `}
                data-testid={`live-momento-option-${bucket}`}
                data-active={isActive}
              >
                <Icon className="w-8 h-8" />
                <span className="text-lg">{BUCKET_LABELS[bucket]}</span>
                <span className="text-xs opacity-80 tabular-nums">
                  {counts[bucket] || 0} pet.
                </span>
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LiveMomentoSwitcher;
